'use client';

import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { zodResolver } from '@hookform/resolvers/zod';
import { sendGAEvent, sendGTMEvent } from '@next/third-parties/google';
import { useReCaptcha } from 'next-recaptcha-v3';
import styled from 'styled-components';

import { contactSchema, IContact } from './schema';

import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import Success from '@/components/Success/Success';
import { devices } from '@/styles/breakpoints';
import { RegularText, TitleH2 } from '@/styles/typography';

export const ContactForm = () => {
  const [hasSuccessfullySentMail, setHasSuccessfullySentMail] = useState(false);
  const [hasErrored, setHasErrored] = useState(false);

  const { executeRecaptcha } = useReCaptcha();
  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<FormData>({
    resolver: zodResolver(contactSchema),
  });

  async function onSubmit(payload: IContact) {
    try {
      const token = await executeRecaptcha('form_submit');

      await fetch('/api/form/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          referer: 'https://orcatec.com',
          // 'x-api-key': 'd6SlHdfwFoGjAJ5r8GptcUYr6zMX3Nfj',
        },
        body: JSON.stringify({
          subject: 'Contact form',
          data: payload,
          token,
        }),
      });

      sendGTMEvent({
        event: 'form_sent',
        value: process.env.NEXT_PUBLIC_GOOGLE_TAG,
      });
      sendGAEvent([{ event: 'form_sent' }]);
    } catch {
      setHasErrored(true);
      return;
    }

    setHasSuccessfullySentMail(true);
  }

  const isSent = isSubmitSuccessful;
  const isDisabled = isSubmitting || isSent;
  const isSubmitDisabled = Object.keys(errors).length > 0 || isDisabled;

  if (hasSuccessfullySentMail) {
    return <Success />;
  }

  return (
    <Wrapper>
      <Title>Contact Us</Title>
      <Description>
        Fill out the form below to receive expert consultation. Don’t miss this
        opportunity to transform{' '}
      </Description>
      <Form
        action=""
        method="POST"
        onSubmit={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleSubmit(onSubmit)(e);
          return false;
        }}
      >
        {hasErrored && (
          <ErrorMessage>
            Couldn&apos;t send email. Please try again.
          </ErrorMessage>
        )}

        <InputStack>
          <LabelMessage error={!!errors?.full_name}>
            <label htmlFor="full_name">
              Full Name<span className="required">*</span>
            </label>
          </LabelMessage>
          <Input
            id="full_name"
            disabled={isDisabled}
            {...register('full_name', { required: true })}
            error={!!errors?.full_name}
          />
          {errors?.full_name && <ErrorMessage>Name is required</ErrorMessage>}
        </InputStack>

        <InputGroup>
          <InputStack>
            <LabelMessage error={!!errors?.phone}>
              <label htmlFor="phone">
                Phone<span className="required">*</span>
              </label>
            </LabelMessage>
            <Controller
              name="phone"
              control={control}
              defaultValue={''}
              render={({ field: { value, onChange, onBlur } }) => (
                <InputMask
                  mask="(999) 999-9999"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                >
                  {() => <Input id="phone" error={!!errors?.phone} />}
                </InputMask>
              )}
            />
            {errors?.phone && <ErrorMessage>Phone is required</ErrorMessage>}
          </InputStack>
          <InputStack>
            <LabelMessage error={!!errors?.email}>
              <label htmlFor="email">
                Email<span className="required">*</span>
              </label>
            </LabelMessage>
            <Input
              id="email"
              disabled={isDisabled}
              {...register('email', { required: true })}
              error={!!errors?.email}
            />

            {errors?.email && <ErrorMessage>Email is required</ErrorMessage>}
          </InputStack>
        </InputGroup>

        <InputStack>
          <LabelMessage>
            <label htmlFor="message">Message</label>
          </LabelMessage>
          <Textarea
            as="textarea"
            id="message"
            disabled={isDisabled}
            {...register('message')}
          />
          {errors?.message && (
            <ErrorMessage>Description is required</ErrorMessage>
          )}
          <CapchaBlock>
            This site is protected by reCAPTCHA and the Google{' '}
            <a target="_blank" href="https://policies.google.com/privacy">
              Privacy Policy{' '}
            </a>{' '}
            and{' '}
            <a target="_blank" href="https://policies.google.com/terms">
              Terms of Service{' '}
            </a>{' '}
            apply.
          </CapchaBlock>
        </InputStack>
        <ButtonWrapper>
          <Button variant="secondary" type="submit" disabled={isSubmitDisabled}>
            Send request
          </Button>
        </ButtonWrapper>
      </Form>
    </Wrapper>
  );
};

const CapchaBlock = styled.small`
  padding-top: 4px;
  text-align: left;
  display: block;
  color: #4d4d4d;
  font-size: 12px;

  & a {
    color: #206fed;
    font-size: 12px;
    text-decoration: none;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  @media only screen and ${devices.desktop} {
    padding: 56px;
  }
  & textarea {
    resize: none;
  }
`;

const Title = styled(TitleH2)`
  margin-bottom: 16px;
  @media only screen and ${devices.desktop} {
    text-align: left;
    font-size: 32px;
  }
`;

const Description = styled(RegularText)`
  margin-bottom: 16px;
`;

const Form = styled.form``;

const ButtonWrapper = styled.div`
  @media only screen and ${devices.desktop} {
    display: flex;
    align-items: center;
    justify-content: start;
    padding-top: 16px;
    & button {
      width: 168px;
    }
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;

  & input {
    width: 100%;
  }

  @media only screen and ${devices.desktop} {
    display: flex;
    flex-direction: row;
    gap: 16px;
  }
`;

const InputStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 16px;
  & .required {
    color: var(--errorColor);
    margin-left: 4px;
  }
`;

const ErrorMessage = styled.p`
  color: var(--errorColor);
  font-family: var(--font-roboto);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
  padding-top: 4px;
`;

const LabelMessage = styled.p`
  color: ${(props) => (props.error ? 'red' : 'var(--greyColor)')};
  font-family: var(--font-roboto);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
`;

const Textarea = styled(Input)`
  width: 100%;
  min-height: 80px;
`;
